import React, { useMemo } from "react";

const STATUS_COLORS = {
  PRESENT: "#0d9488",
  ABSENT: "#94a3b8",
  LATE: "#f59e0b",
  LEAVE: "#0ea5e9",
  HALF_DAY: "#a78bfa",
};

const BUCKET_COLORS = ["#0d9488", "#0ea5e9", "#6366f1", "#f59e0b", "#94a3b8", "#a78bfa"];

function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  if (endDeg - startDeg >= 360) {
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`;
  }
  const start = polar(cx, cy, r, endDeg);
  const end = polar(cx, cy, r, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${end.x} ${end.y} A ${r} ${r} 0 ${large} 1 ${start.x} ${start.y} Z`;
}

export function StatusDonutChart({ breakdown = {}, size = 148, bucketMode = false }) {
  const slices = useMemo(() => {
    return Object.entries(breakdown)
      .map(([status, value], idx) => ({
        status,
        label: bucketMode ? status.replace(/^\d{4}-W/, "W") : status.replace(/_/g, " "),
        value: Number(value ?? 0),
        color: bucketMode
          ? BUCKET_COLORS[idx % BUCKET_COLORS.length]
          : STATUS_COLORS[status] ?? "#cbd5e1",
      }))
      .filter((s) => s.value > 0);
  }, [breakdown, bucketMode]);

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;

  let angle = 0;
  const arcs = slices.map((slice) => {
    const sweep = total > 0 ? (slice.value / total) * 360 : 0;
    const start = angle;
    angle += sweep;
    return { ...slice, path: arcPath(cx, cy, r, start, start + sweep) };
  });

  if (total === 0) {
    return (
      <div className="att-rpt-chart-empty">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <circle cx={cx} cy={cy} r={r} fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="2" />
        </svg>
        <p>No data for this range</p>
      </div>
    );
  }

  return (
    <div className="att-rpt-donut att-rpt-donut--responsive">
      <svg
        className="att-rpt-donut__svg"
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Attendance status breakdown"
      >
        {arcs.map((a) => (
          <path key={a.status} d={a.path} fill={a.color} />
        ))}
        <circle cx={cx} cy={cy} r={r * 0.58} fill="#fff" />
        <text x={cx} y={cy - 4} textAnchor="middle" className="att-rpt-donut__total">
          {total}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="att-rpt-donut__sub">
          {bucketMode ? "total" : "records"}
        </text>
      </svg>
      <ul className="att-rpt-donut__legend">
        {arcs.map((a) => (
          <li key={a.status}>
            <span className="att-rpt-donut__swatch" style={{ background: a.color }} />
            <span>{a.label}</span>
            <strong>{a.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PresenceBarChart({ labels = [], values = [] }) {
  const data = useMemo(() => {
    const max = Math.max(1, ...values, 0);
    return labels.map((label, i) => ({
      label,
      count: values[i] ?? 0,
      pct: (values[i] ?? 0) / max,
    }));
  }, [labels, values]);

  const width = 640;
  const height = 200;
  const padL = 28;
  const padR = 8;
  const padT = 12;
  const padB = 28;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(data.length, 1);
  const barGap = 2;
  const barW = Math.max(4, chartW / n - barGap);

  const hasData = data.some((d) => d.count > 0);

  return (
    <div className="att-rpt-bars-wrap">
      <svg
        className="att-rpt-bars"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Attendance presence bar chart"
      >
        <line x1={padL} y1={padT + chartH} x2={width - padR} y2={padT + chartH} stroke="#e2e8f0" />
        {[0, 0.5, 1].map((t) => {
          const y = padT + chartH - t * chartH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#f1f5f9" />
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = d.pct * chartH;
          const x = padL + i * (barW + barGap);
          const y = padT + chartH - h;
          const active = d.count > 0;
          const showLabel = n <= 12 || i === 0 || i === n - 1 || i % Math.ceil(n / 8) === 0;
          return (
            <g key={`${d.label}-${i}`}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(h, active ? 3 : 0)}
                rx="2"
                fill={active ? "url(#attBarGrad)" : "#f1f5f9"}
              />
              {showLabel ? (
                <text x={x + barW / 2} y={height - 8} textAnchor="middle" className="att-rpt-bars__label">
                  {d.label}
                </text>
              ) : null}
            </g>
          );
        })}
        <defs>
          <linearGradient id="attBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2dd4bf" />
            <stop offset="100%" stopColor="#0d9488" />
          </linearGradient>
        </defs>
      </svg>
      {!hasData ? <p className="att-rpt-chart-empty__hint">No presence data for this range.</p> : null}
    </div>
  );
}

export function WorkedMinutesSparkline({ labels = [], values = [], latest = 0 }) {
  const points = useMemo(() => {
    const vals = values.map((v) => Number(v ?? 0));
    const max = Math.max(1, ...vals, 480);
    const w = 640;
    const h = 120;
    if (vals.length < 2) {
      return { path: "", area: "", w, h, hasData: vals.some((v) => v > 0), latest: latest ?? vals[0] ?? 0 };
    }
    const coords = vals.map((v, i) => ({
      x: (i / (vals.length - 1)) * w,
      y: h - (v / max) * (h - 8),
    }));
    const line = coords.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    const area = `${line} L ${w} ${h} L 0 ${h} Z`;
    return { path: line, area, w, h, hasData: vals.some((v) => v > 0), latest: latest ?? vals[vals.length - 1] ?? 0 };
  }, [values, latest]);

  return (
    <div className="att-rpt-spark">
      <div className="att-rpt-spark__head">
        <strong>Worked minutes trend</strong>
        <span>{points.latest} min latest</span>
      </div>
      <svg
        viewBox={`0 0 ${points.w} ${points.h}`}
        className="att-rpt-spark__svg"
        preserveAspectRatio="none"
        role="img"
        aria-label="Worked minutes trend"
      >
        {points.hasData && points.area ? (
          <>
            <path d={points.area} fill="rgba(14, 165, 233, 0.15)" />
            <path d={points.path} fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" />
          </>
        ) : (
          <line x1="0" y1={points.h / 2} x2={points.w} y2={points.h / 2} stroke="#e2e8f0" strokeDasharray="4 4" />
        )}
      </svg>
      {labels.length > 0 ? (
        <div className="att-rpt-spark__labels" aria-hidden>
          <span>{labels[0]}</span>
          <span>{labels[labels.length - 1]}</span>
        </div>
      ) : null}
    </div>
  );
}

/** @deprecated use PresenceBarChart */
export function DailyPresenceBarChart(props) {
  const daysInMonth = new Date(props.year, props.month, 0).getDate();
  const labels = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
  return <PresenceBarChart labels={labels} values={props.series ?? []} />;
}
